/**
 * Add an expense.
 *
 * Participants are chosen PER EXPENSE and are never assumed to be all three
 * owners (docs/01 §7.2, and §12 "Settled"). Guests may be included — they
 * affect the arithmetic and are then marked settled-on-the-day, so their share
 * never reaches a balance.
 *
 * Every split is computed by `splitExpense`, never in this file. That is what
 * makes the live preview trustworthy: the shares shown are the shares that will
 * be stored, and they sum to the total EXACTLY for equal, exact and shares
 * alike — the remainder piastre goes somewhere specific rather than being
 * rounded away.
 */

import { type FormEvent, useMemo, useState } from 'react'
import {
  type ExpenseSplit,
  type MemberId,
  type Piastres,
  type SplitKind,
  type TrustedClock,
  piastres,
} from '@office/domain'
import {
  MoneyError,
  type SplitParticipant,
  splitExpense,
  splitTotal,
} from '@office/domain/money'
import type { MoneyActions } from './actions.js'
import { type MoneyPerson, type MoneySnapshot, nameOf } from './data.js'
import { formatInstant, parseAmount } from './format.js'
import { AmountInput, Banner, Field, Money, RadioGroup } from './ui.js'
import './money.css'

const SPLIT_KINDS: readonly { value: SplitKind; label: string }[] = [
  { value: 'equal', label: 'Equal' },
  { value: 'exact', label: 'Exact' },
  { value: 'shares', label: 'Shares' },
]

export interface ExpenseFormProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  actorId: MemberId
  actions: MoneyActions
  onDone?: () => void
}

interface Preview {
  splits: ExpenseSplit[] | null
  error: string | null
  /** total - sum(splits); zero when the split is well formed. */
  unallocated: Piastres
}

export function ExpenseForm({ snapshot, clock, actorId, actions, onDone }: ExpenseFormProps) {
  const now = clock.now()

  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('general')
  const [amountText, setAmountText] = useState('')
  const [splitKind, setSplitKind] = useState<SplitKind>('equal')
  // Null means "nobody has touched the picker yet", in which case the default
  // is every owner. Seeding the set at mount instead would freeze whatever the
  // local mirror happened to have read by then — on a cold start, nobody.
  const [picked, setPicked] = useState<ReadonlySet<string> | null>(null)
  const chosen: ReadonlySet<string> = useMemo(
    () => picked ?? new Set(snapshot.members.map(m => m.id)),
    [picked, snapshot.members],
  )
  const [exact, setExact] = useState<Record<string, string>>({})
  const [weights, setWeights] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const amount = parseAmount(amountText)

  /** Participant order follows the (name-sorted) people list, so the remainder
   *  piastre lands in the same place on every device. */
  const participants: SplitParticipant[] = useMemo(
    () =>
      snapshot.people
        .filter(p => chosen.has(p.id))
        .map(p => {
          const base: SplitParticipant = {
            personId: p.id,
            ...(p.kind === 'guest' ? { settledImmediately: true } : {}),
            ...(splitKind === 'exact' ? { exactAmount: parseAmount(exact[p.id] ?? '') ?? piastres(0) } : {}),
            ...(splitKind === 'shares' ? { shares: weightOf(weights[p.id]) } : {}),
          }
          return base
        }),
    [snapshot.people, chosen, splitKind, exact, weights],
  )

  const preview: Preview = useMemo(() => {
    if (amount === null || participants.length === 0) {
      return { splits: null, error: null, unallocated: piastres(0) }
    }
    try {
      const splits = splitExpense(amount, splitKind, participants)
      return { splits, error: null, unallocated: piastres(amount - splitTotal(splits)) }
    } catch (e) {
      const allocated = participants.reduce((acc, p) => acc + (p.exactAmount ?? 0), 0)
      return {
        splits: null,
        error: e instanceof MoneyError ? messageFor(e) : 'That split does not work',
        unallocated: piastres(amount - allocated),
      }
    }
  }, [amount, splitKind, participants, snapshot])

  const canSubmit =
    description.trim().length > 0 &&
    amount !== null &&
    amount > 0 &&
    preview.splits !== null &&
    !busy

  function toggle(id: string) {
    const next = new Set(chosen)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit || amount === null || preview.splits === null) return
    setBusy(true)
    setError(null)
    try {
      await actions.addExpense({
        description: description.trim(),
        amount,
        category: category.trim() || 'general',
        splitKind,
        splits: preview.splits,
        source: 'manual',
        sourceRef: null,
      })
      // This form stays mounted after a save, and a manual expense has no
      // natural key the server could dedupe on: a second tap on a still-filled
      // form is a second, indistinguishable expense in the ledger. Clear the
      // draft so what is on screen is what is unsaved.
      setDescription('')
      setAmountText('')
      setCategory('general')
      setPicked(null)
      setExact({})
      setWeights({})
      onDone?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that expense')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="m-screen" onSubmit={submit} aria-labelledby="expense-title">
      <h2 className="m-title" id="expense-title">Add an expense</h2>
      <p className="m-asof">Paid by {nameOf(snapshot, actorId)} · {formatInstant(now)}</p>

      <Field id="expense-description" label="What was it?">
        <input
          id="expense-description"
          className="m-input"
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </Field>

      <AmountInput
        id="expense-amount"
        label="Amount (EGP)"
        value={amountText}
        onChange={setAmountText}
        invalid={amountText.length > 0 && amount === null}
        {...(amountText.length > 0 && amount === null
          ? { hint: 'Enter an amount like 250 or 250.50' }
          : {})}
      />

      <Field id="expense-category" label="Category">
        <input
          id="expense-category"
          className="m-input"
          type="text"
          value={category}
          onChange={e => setCategory(e.target.value)}
        />
      </Field>

      <fieldset className="m-fieldset">
        <legend className="m-legend">Who is in on this?</legend>
        <div className="m-stack">
          {snapshot.people.map(p => (
            <ParticipantRow
              key={p.id}
              person={p}
              checked={chosen.has(p.id)}
              onToggle={() => toggle(p.id)}
              splitKind={splitKind}
              exactValue={exact[p.id] ?? ''}
              onExact={v => setExact(prev => ({ ...prev, [p.id]: v }))}
              weightValue={weights[p.id] ?? '1'}
              onWeight={v => setWeights(prev => ({ ...prev, [p.id]: v }))}
            />
          ))}
        </div>
      </fieldset>

      <RadioGroup
        legend="Split"
        name="expense-split-kind"
        options={SPLIT_KINDS}
        value={splitKind}
        onChange={setSplitKind}
      />

      <div className="m-preview" aria-live="polite" data-testid="split-preview">
        {participants.length === 0 ? (
          <p className="m-empty-note">Pick at least one person to split this with.</p>
        ) : amount === null ? (
          <p className="m-empty-note">Enter an amount to see the split.</p>
        ) : (
          <table className="m-table">
            <caption className="m-caption">Split preview</caption>
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Share</th>
              </tr>
            </thead>
            <tbody>
              {(preview.splits ?? []).map(s => (
                <tr key={s.personId}>
                  <th scope="row">
                    {nameOf(snapshot, s.personId)}
                    {s.settledImmediately && <span className="m-tag"> guest · settles today</span>}
                  </th>
                  <td><Money value={s.shareAmount} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Splits total</th>
                <td data-testid="splits-total">
                  <Money value={preview.splits ? splitTotal(preview.splits) : piastres(0)} />
                </td>
              </tr>
              <tr>
                <th scope="row">Expense total</th>
                <td><Money value={amount} /></td>
              </tr>
            </tfoot>
          </table>
        )}

        {preview.splits !== null && (
          <p className="m-ok-line">Splits add up to the total exactly.</p>
        )}
        {preview.error !== null && (
          <Banner tone="warn">
            {preview.error}
            {preview.unallocated !== 0 && (
              <>
                {' '}
                {preview.unallocated > 0 ? 'Still to allocate: ' : 'Over by: '}
                <Money value={piastres(Math.abs(preview.unallocated))} />
              </>
            )}
          </Banner>
        )}
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="m-actions">
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          Save expense
        </button>
      </div>
    </form>
  )
}

function ParticipantRow({
  person,
  checked,
  onToggle,
  splitKind,
  exactValue,
  onExact,
  weightValue,
  onWeight,
}: {
  person: MoneyPerson
  checked: boolean
  onToggle: () => void
  splitKind: SplitKind
  exactValue: string
  onExact: (v: string) => void
  weightValue: string
  onWeight: (v: string) => void
}) {
  const exactId = `exact-${person.id}`
  const weightId = `weight-${person.id}`
  const guestNoteId = `guest-note-${person.id}`
  const isGuest = person.kind === 'guest'
  return (
    <div className="m-choice">
      <label className="m-choice-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          // Described, not renamed: being a guest is the whole reason this
          // person's share behaves differently, and reading it off an adjacent
          // span is a sighted-only affordance.
          aria-describedby={isGuest ? guestNoteId : undefined}
        />
        <span className="m-choice-main">{person.displayName}</span>
      </label>
      {isGuest && <span className="m-tag" id={guestNoteId}>guest · settles today</span>}
      {checked && splitKind === 'exact' && (
        <span className="m-inline-field">
          <label className="m-hint" htmlFor={exactId}>{person.displayName}&rsquo;s amount</label>
          <input
            id={exactId}
            className="m-input m-input-narrow"
            type="text"
            inputMode="decimal"
            value={exactValue}
            onChange={e => onExact(e.target.value)}
          />
        </span>
      )}
      {checked && splitKind === 'shares' && (
        <span className="m-inline-field">
          <label className="m-hint" htmlFor={weightId}>{person.displayName}&rsquo;s shares</label>
          <input
            id={weightId}
            className="m-input m-input-narrow"
            type="number"
            min={0}
            step={1}
            value={weightValue}
            onChange={e => onWeight(e.target.value)}
          />
        </span>
      )}
    </div>
  )
}

function weightOf(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return 1
  const n = Number(raw)
  return Number.isSafeInteger(n) && n >= 0 ? n : -1 // -1 makes the domain reject it loudly
}

function messageFor(e: MoneyError): string {
  switch (e.code) {
    case 'EXACT_SUM_MISMATCH':
      return 'The exact amounts do not add up to the total.'
    case 'MISSING_EXACT_AMOUNT':
      return 'Every person needs an exact amount.'
    case 'ZERO_SHARES':
      return 'Someone needs at least one share.'
    case 'BAD_SHARES':
      return 'Shares must be whole numbers, zero or more.'
    case 'EMPTY_PARTICIPANTS':
      return 'Pick at least one person to split this with.'
    case 'DUPLICATE_PARTICIPANT':
      return 'Somebody is in the split twice.'
    default:
      return 'That split does not work.'
  }
}
